/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReviewButton } from '@/pages/MyReviewsPage';
import { URGENCY_BADGE_CLS } from './parts';

export const AppointmentMiniRow = ({ a, onOpenRoom }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'it';
  const dt = a.scheduled_at ? new Date(a.scheduled_at) : null;
  const dtLabel = dt ? dt.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' }) : '-';
  const now = new Date();
  const canJoin = dt && (dt.getTime() - 10 * 60000) <= now.getTime() && now.getTime() <= (dt.getTime() + 60 * 60000);
  const urg = a.urgency || 'routine';
  const urgCls = URGENCY_BADGE_CLS[urg] || URGENCY_BADGE_CLS.routine;

  const statusMap = {
    confirmed: 'badge-online',
    awaiting_payment: 'badge-waiting',
    in_progress: 'badge-online',
    completed: '',
    cancelled: 'badge-emergency',
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl border border-border/60 bg-card" data-testid={`appointment-row-${a.id}`}>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate">Dr. {a.doctor_name}</div>
        <div className="text-xs text-muted-foreground">{dtLabel}</div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <span className={cn('text-[10px] px-2 py-0.5 rounded-full uppercase font-bold', statusMap[a.status] || 'bg-muted text-foreground')}>
            {t(`telemedStatus_${a.status}`, a.status)}
          </span>
          <span className={cn('text-[10px] px-2 py-0.5 rounded-full uppercase font-bold', urgCls)}>
            {t(`urgency${urg.charAt(0).toUpperCase() + urg.slice(1)}`, urg)}
          </span>
          {a.is_free_consultation ? (
            <span className="text-[10px] text-muted-foreground">{t('telemedFreeTag')}</span>
          ) : (
            <span className="text-[10px] text-muted-foreground">€ {a.visit_price_eur?.toFixed(2)}</span>
          )}
        </div>
      </div>
      {canJoin && (a.status === 'confirmed' || a.status === 'in_progress') && (a.payment_status === 'paid' || a.payment_status === 'not_required') && (
        <Button size="sm" variant="accent" onClick={() => onOpenRoom(a.id)} className="pulse-glow" data-testid={`join-room-btn-${a.id}`}>
          <Zap className="h-3.5 w-3.5" /> {t('bookJoinNow')}
        </Button>
      )}
      {(a.status === 'completed' || a.status === 'ended') && (
        <ReviewButton appointmentId={a.id} doctorName={`Dr. ${a.doctor_name || ''}`} />
      )}
    </div>
  );
};
